import { useMutation } from '@tanstack/react-query';
import { useMutationInvalidator } from '~/hooks/useMutationInvalidator';
import { simulation, simulationV2 } from '~/services/simulation-service';
import { useStore, useStoreActions } from '~/store';
import { getPayloadForSimulationType } from '~/store/simulation/utils';
import { resolveSimulationServiceVersion } from '~/store/simulation/resolveSimulationServiceVersion';
import { SimulationStatus } from '~/store/simulation/types';
import type {
  SimulationServiceVersion,
  Simulation,
  SimulationUpdate,
  SimulationContextMapping,
  SimulationContextMappingCreate,
  SimulationSetupData,
} from '~/store/simulation/types';

const httpStatus = (e: unknown): number | undefined => (e as { response?: { status?: number } })?.response?.status;
const is404 = (e: unknown): boolean => httpStatus(e) === 404;

// The simulation API rejects request bodies over 1 MiB, which an oversized dataset selection hits.
// No retry gets under the limit, so the message names the cause and the way out instead.
const SETUP_DATA_TOO_LARGE_MESSAGE =
  'This selection is too large to save. Select fewer dataset files, or split them across simulations.';

// Resolve at call time, not render: the picker's stamp only reaches the store after the create
// save, so a render-time read would mis-route a v1 create. Payload stamp wins, else the store's.
const useResolveVersionAtCallTime = () => {
  const store = useStore();
  return (payloadSetupData?: SimulationSetupData): SimulationServiceVersion =>
    resolveSimulationServiceVersion(
      payloadSetupData?.serviceVersion ? payloadSetupData : store.getState().simulation.simulationSetupData,
    );
};

// `tooLarge` is opt-in: only the mutations that can actually send an oversized body should tell the
// user to trim their selection.
const useSetupErrorNotifier = () => {
  const dispatchMessage = useStoreActions((actions) => actions.messages.dispatchMessage);
  return (content: string, tooLarge?: string) => (e: unknown) => {
    const message = tooLarge && httpStatus(e) === 413 ? tooLarge : content;
    console.error(message, e);
    dispatchMessage({ noticeType: 'error', content: message });
  };
};

// The wizard reads detail/sim from Easy Peasy, so RQ invalidations are inert here; mirror the
// legacy thunk's store refresh in onSuccess.
const useSimulationStoreSync = () => {
  const setSimulation = useStoreActions((actions) => actions.simulation.setSimulation);
  const fetchSimulationDetail = useStoreActions((actions) => actions.simulation.fetchSimulationDetail);
  return { setSimulation, fetchSimulationDetail };
};

// Legacy v1 sims have no stamp → default-resolve v2 → v2 404. Re-stamp 'v1' before the v1 fallback
// so the persisted setupData ends up truthful.
const restampV1 = (sim: SimulationUpdate): SimulationUpdate =>
  sim.simulationSetupData ? { ...sim, simulationSetupData: { ...sim.simulationSetupData, serviceVersion: 'v1' } } : sim;

// Each mutationFn routes v1/v2 at call time; a v2 404 falls back to v1 (mirrors the read path).

export function useCreateSimulation() {
  const resolveVersion = useResolveVersionAtCallTime();
  const notify = useSetupErrorNotifier();
  const { setSimulation } = useSimulationStoreSync();
  // No 404 fallback: nothing pre-exists to find on create.
  const fn = (sim: Partial<Simulation>) =>
    resolveVersion(sim.simulationSetupData) === 'v1' ? simulation.createV1(sim) : simulationV2.create(sim);
  return useMutation<Simulation, unknown, Partial<Simulation>>(useMutationInvalidator(fn), {
    // detail is fetched by SimulationCreatePage once the simulationId lands
    onSuccess: (data) => setSimulation(data),
    onError: notify('Error saving simulation. Please retry.'),
  });
}

export function useUpdateSimulation() {
  const resolveVersion = useResolveVersionAtCallTime();
  const notify = useSetupErrorNotifier();
  const { setSimulation, fetchSimulationDetail } = useSimulationStoreSync();
  const fn = async (sim: SimulationUpdate) => {
    if (resolveVersion(sim.simulationSetupData) === 'v1') return simulation.updateV1(sim);
    try {
      return await simulationV2.update(sim);
    } catch (e) {
      if (!is404(e)) throw e;
      return simulation.updateV1(restampV1(sim));
    }
  };
  return useMutation<Simulation, unknown, SimulationUpdate>(useMutationInvalidator(fn), {
    onSuccess: (data) => {
      setSimulation(data);
      fetchSimulationDetail(data.simulationId);
    },
    onError: notify('Error saving simulation. Please retry.', SETUP_DATA_TOO_LARGE_MESSAGE),
  });
}

// Callers pass a flat link[] (each carries simulationId); v2 wants { simulationId, mappings }.
const contextMappingsV2Adapter = (links: SimulationContextMappingCreate[]) => {
  const simulationId = links[0]?.simulationId;
  if (!simulationId) {
    throw new Error('Unable to create context mappings. No simulation detected');
  }
  return simulationV2.createContextMappings({ simulationId, mappings: links });
};

export function useCreateContextMappings() {
  const resolveVersion = useResolveVersionAtCallTime();
  const notify = useSetupErrorNotifier();
  // Links carry no setupData, so resolution uses the store stamp.
  const fn = async (links: SimulationContextMappingCreate[]) => {
    if (resolveVersion() === 'v1') return simulation.contextMappingsV1(links);
    try {
      return await contextMappingsV2Adapter(links);
    } catch (e) {
      if (!is404(e)) throw e;
      return simulation.contextMappingsV1(links);
    }
  };
  return useMutation<SimulationContextMapping[], unknown, SimulationContextMappingCreate[]>(
    useMutationInvalidator(fn),
    { onError: notify('Error saving simulation mappings. Please retry.') },
  );
}

export function useStartSimulation() {
  const resolveVersion = useResolveVersionAtCallTime();
  const notify = useSetupErrorNotifier();
  const fn = async (sim: Simulation) => {
    // v1 needs the asset payload built here (see simulation.startV1); v2 start is body-less.
    if (resolveVersion(sim.simulationSetupData) === 'v1') {
      return simulation.startV1(sim, getPayloadForSimulationType(sim));
    }
    try {
      return await simulationV2.start(sim.simulationId);
    } catch (e) {
      if (!is404(e)) throw e;
      return simulation.startV1(sim, getPayloadForSimulationType(sim));
    }
  };
  return useMutation<Simulation, unknown, Simulation>(useMutationInvalidator(fn), {
    onError: notify('Error starting simulation. Please retry.'),
  });
}

// Archive: v2 has no DELETE, so it's a status flip (simulationV2.update); v1 keeps the DELETE thunk.
// v2-404 falls back to v1 (legacy unstamped sims).
export function useArchiveSimulation() {
  const resolveVersion = useResolveVersionAtCallTime();
  const notify = useSetupErrorNotifier();
  const archiveV1Thunk = useStoreActions((actions) => actions.simulation.archiveSimulation);
  const fn = async (vars: { simulationId: string; simulationSetupData?: SimulationSetupData }) => {
    // v1 thunk self-notifies; only v2 notifies here (no blanket onError → avoids a double toast).
    const runV1 = async () => {
      await archiveV1Thunk(vars.simulationId);
      return { data: undefined, invalidateQueryKeys: [] as string[][] };
    };
    if (resolveVersion(vars.simulationSetupData) === 'v1') return runV1();
    try {
      return await simulationV2.update({
        simulationId: vars.simulationId,
        simulationStatus: SimulationStatus.ARCHIVED,
      });
    } catch (e) {
      if (is404(e)) return runV1();
      notify('Error archiving simulation. Please retry.')(e);
      throw e;
    }
  };
  return useMutation<unknown, unknown, { simulationId: string; simulationSetupData?: SimulationSetupData }>(
    useMutationInvalidator(fn),
  );
}
